import { useMemo } from 'react';
import { useAuth } from 'contexts/ProtectedRoute';
import { Role } from 'data/constants/roles';
import { AnalyticsRoleType } from 'hooks/analytics';

/**
 * Source of truth for the role-type tab logic used by Team Health and
 * Practitioner Speeds. Keep this in sync with the backend allow-list so
 * we never request a role_type the current user is not permitted to see.
 */
const ALLOWED_TABS_BY_ROLE: Record<string, AnalyticsRoleType[]> = {
    [Role.ADMIN]: ['consultant'],
    [Role.BACKOFFICE_ADMIN]: ['backoffice', 'sales'],
    [Role.SUPER_ADMIN]: ['consultant', 'backoffice', 'sales'],
    [Role.SUPER_SUPER_ADMIN]: ['consultant', 'backoffice', 'sales'],
};

const DEFAULT_TAB_BY_ROLE: Record<string, AnalyticsRoleType> = {
    [Role.ADMIN]: 'consultant',
    [Role.BACKOFFICE_ADMIN]: 'backoffice',
    [Role.SUPER_ADMIN]: 'consultant',
    [Role.SUPER_SUPER_ADMIN]: 'consultant',
};

export const ROLE_TYPE_LABELS: Record<AnalyticsRoleType, string> = {
    consultant: 'Consultants',
    backoffice: 'Backoffice',
    sales: 'Sales',
};

export interface RoleTypeTabsConfig {
    role: string;
    allowedTabs: AnalyticsRoleType[];
    defaultTab: AnalyticsRoleType | null;
    canAccess: (tab: AnalyticsRoleType) => boolean;
}

const useRoleTypeTabs = (): RoleTypeTabsConfig => {
    const { role } = useAuth();
    return useMemo(() => {
        const allowedTabs = ALLOWED_TABS_BY_ROLE[role] ?? [];
        const preferred = DEFAULT_TAB_BY_ROLE[role];
        const defaultTab =
            allowedTabs.length === 0
                ? null
                : preferred && allowedTabs.includes(preferred)
                  ? preferred
                  : allowedTabs[0];
        return {
            role,
            allowedTabs,
            defaultTab,
            canAccess: (tab) => allowedTabs.includes(tab),
        };
    }, [role]);
};

export default useRoleTypeTabs;
